import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { AppBar, Button, createStyles, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, makeStyles, Paper, Slide, Snackbar, Tab, Tabs, Theme, Toolbar, Typography, Chip, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, TextField } from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import CloseIcon from '@material-ui/icons/Close';
import { Address, ClaimStatus, PropertyOwnershipClaim } from '../../Models/Property';
import { connect } from 'react-redux';
import { baseEndpoint } from '../../App';
import { Alert } from '@material-ui/lab';
import RenderDocumentActions from './DocLinksActions';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { CircularProgress } from '@material-ui/core';
import { Answer, AnswerType, Question, ScoreInfo } from '../../Models/Info';
import { verificationQuestions } from '../../Models/VerificationQuestions';
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        appBar: {
            position: 'relative',
            backgroundColor: '#0404fc'
        },
        title: {
            marginLeft: theme.spacing(2),
            flex: 1,
        },
        root: {
            flexGrow: 1,
            padding: "15px",
            textAlign: "left"
        },
        addressContent: {
            display: "flex",
            alignItems: "center",
            width: "60%",  // Control the width of the left side
        },
        downloadIcon: {
            fontSize: 14
        },
        listItemContent: {
            display: "flex",
            width: "90%",
            alignItems: "center",
            justifyContent: "space-between",
            marginLeft: "5%",
            marginRight: "5%"
        },
        docsContainer: {
            width: "38%",  // Adjust this value to control width
            minWidth: "405px",
            marginLeft: "auto",
            marginTop: theme.spacing(2)
        },
        docsList: {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.spacing(1),
            marginLeft: "auto"
        },
        docItem: {
            marginBottom: theme.spacing(1),
            '&.accepted': {
                backgroundColor: 'rgba(23, 215, 3, 0.1)'  // Light green background
            },
            '&.rejected': {
                backgroundColor: 'rgba(255, 51, 51, 0.1)'  // Light red background
            }
        },
        statusIcon: {
            marginLeft: theme.spacing(1),
            verticalAlign: 'middle'
        },
        acceptedText: {
            color: '#17d703',
            display: 'flex',
            alignItems: 'center'
        },
        rejectedText: {
            color: '#ff3333',
            display: 'flex',
            alignItems: 'center'
        },
        docButton: {
            backgroundColor: "#17d703",
            color: "white",
            '&:hover': {
                backgroundColor: "#15c003"
            }
        },
        notesContainer: {
            width: "100%",  // Same width as docs container
            marginLeft: "auto",
            marginTop: theme.spacing(2)
        },
        notesTextArea: {
            width: "90%",
            backgroundColor: '#f5f5f5',
            borderRadius: theme.spacing(1),
            padding: theme.spacing(1),
            border: "none",
            minHeight: "100px",
            fontFamily: "inherit",
            resize: "vertical",
            //Center the textarea
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            '&:focus': {
                outline: `2px solid ${theme.palette.primary.main}`,
            }
        },
        docLink: {
            cursor: 'pointer',
            color: theme.palette.primary.main,
            display: 'block',
            width: '65%',
            wordBreak: 'break-all',
            '&:hover': {
                textDecoration: 'underline'
            }
        },
        acceptButton: {
            backgroundColor: "#17d703",
            color: "white",
            marginRight: theme.spacing(1),
            '&:hover': {
                backgroundColor: "#15c003"
            }
        },
        rejectButton: {
            backgroundColor: "#ff3333",
            color: "white",
            '&:hover': {
                backgroundColor: "#cc0000"
            }
        },
        buttonContainer: {
            width: "90%",
            display: "flex",
            justifyContent: "flex-end",
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: "10px"
        },
        saveButton: {
            backgroundColor: '#0404fc',
            color: 'white',
            '&:hover': {
                backgroundColor: '#0303d9'
            },
            '&:disabled': {
                backgroundColor: '#9999ff'
            }
        },
        statusContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1)
        },
        editIcon: {
            fontSize: '1rem',
            cursor: 'pointer',
            marginLeft: theme.spacing(1),
            color: '#666',
            '&:hover': {
                color: theme.palette.primary.main
            }
        },
        claimActionButtons: {
            display: 'flex',
            gap: theme.spacing(2),
            justifyContent: 'flex-end',  // Align buttons to the right
            marginTop: theme.spacing(3),  // Add more space above buttons
            marginBottom: theme.spacing(2),  // Add space below buttons
            marginRight: '5%',  // Match the existing content margin
        },
        claimAcceptButton: {
            backgroundColor: "#17d703",
            color: "white",
            minWidth: '120px',  // Ensure consistent button width
            '&:hover': {
                backgroundColor: "#15c003"
            }
        },
        claimRejectButton: {
            backgroundColor: "#ff3333",
            color: "white",
            minWidth: '120px',  // Ensure consistent button width
            '&:hover': {
                backgroundColor: "#cc0000"
            }
        },
        statusAccepted: {
            color: '#17d703',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1)
        },
        statusRejected: {
            color: '#ff3333',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1)
        },
        uploadSection: {
            width: "90%",
            margin: "20px auto",
            padding: theme.spacing(2),
            backgroundColor: '#f5f5f5',
            borderRadius: theme.spacing(1),
        },
        uploadButton: {
            backgroundColor: '#0404fc',
            color: 'white',
            margin: theme.spacing(2),
            '&:hover': {
                backgroundColor: '#0303d9'
            },
            '&:disabled': {
                backgroundColor: '#9999ff'
            }
        },
        fileInput: {
            display: 'none',
        },
        uploadButtonContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(2)
        },
        selectedFiles: {
            margin: theme.spacing(2),
            padding: theme.spacing(1),
            backgroundColor: 'white',
            borderRadius: theme.spacing(1),
        },
        emptyDocsMessage: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: theme.spacing(2)
        },
        tabs: {
            backgroundColor: '#f5f5f5',
            marginBottom: theme.spacing(2)
        },
        tabPanel: {
            padding: theme.spacing(2)
        },
        questionList: {
            width: '90%',
            margin: '0 auto'
        },
        accordion: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        accordionSummary: {
            backgroundColor: '#f5f5f5',
        },
        questionItem: {
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
            backgroundColor: '#fff',
            borderRadius: theme.spacing(1),
        },
        scoreChip: {
            marginLeft: theme.spacing(1),
            backgroundColor: '#17d703',
            color: 'white',
        },
        verificationContainer: {
            width: '90%',
            margin: '0 auto',
        },
        answerInput: {
            marginBottom: theme.spacing(2),
        },
        radioGroup: {
            flexDirection: 'row',
        },
        scoreDisplay: {
            width: "80px",
            textAlign: "left",
            position: "relative",
            marginTop: "5px"
        },
        scoreText: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#0404fc",
            fontSize: "1.5rem"
        },
        verificationHeader: {
            display: "flex",
            alignItems: "flex-start",
            marginBottom: theme.spacing(3),
            gap: theme.spacing(2)
        },
        scoreInfo: {
            marginLeft: theme.spacing(2),
            marginTop: theme.spacing(1)
        },
        scoreDescription: {
            color: "#17d703",
            textAlign: "left"
        }
    }),
);


export interface LandlordClaimsViewProps {
    open: boolean;
    onClose: () => void;
    claim: PropertyOwnershipClaim | null;
    userState: {
        user: {
            currentToken: string;
            category: number;
            internalId: number;
        }
    };
    onClaimUpdated: (claim: PropertyOwnershipClaim) => void;
    questions: Question[];
    answers: Answer[];
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function LandlordClaimsView(props: LandlordClaimsViewProps) {
    const SNACKBAR_DURATION = 3000;
    const STREET_VIEW_SIZE = '115x115';
    const STREET_VIEW_FOV = '90';
    const isAdmin = props.userState.user.category === 1;
    const classes = useStyles();
    const [claim, setClaim] = useState<PropertyOwnershipClaim | null>(props.claim);
    const [streetViewUrl, setStreetViewUrl] = useState<string>("");
    const config = require("../../env.json");
    const [claimNotes, setClaimNotes] = useState<string>("");
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    const [isSuccess, setIsSuccess] = useState(true);
    const [alertMessage, setAlertMessage] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isUpdatingClaim, setIsUpdatingClaim] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [verificationInfo, setVerificationInfo] = useState<ScoreInfo[]>([]);
    const [verificationAnswers, setVerificationAnswers] = useState<Answer[]>([]);



    useEffect(() => {
        if (props.claim?.address) {
            setStreetViewUrl(constructStreetViewUrl(props.claim.address, config.GOOGLE_API_KEY));
            setClaim(props.claim);
            setClaimNotes(props.claim.notes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.claim]);

    useEffect(() => {
        if (props.questions && verificationInfo.length === 0) {
            const info = props.questions
                .filter(q => q.questionType === 3 && q.parentQuestionId != null)
                .map(q => ({
                    questionDescription: q.description,
                    questionId: q.internalId
                } as ScoreInfo));
            setVerificationInfo(info);
        }
    }, [props.questions, verificationInfo.length]);

    const constructStreetViewUrl = (address: Address, apiKey: string) => {
        const streetViewLat = address.streetLat;
        const streetViewLng = address.streetLng;
        return `https://maps.googleapis.com/maps/api/streetview?size=${STREET_VIEW_SIZE}&location=${streetViewLat},${streetViewLng}&source=outdoor&heading=${address.heading}&fov=${STREET_VIEW_FOV}&&key=${apiKey}`;
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleDownload = async (docId: number, docName: string) => {
        try {
            const response = await fetch(`${baseEndpoint}propertyownershipclaims/download/${docId}`, {
                headers: {
                    "Authorization": "Bearer " + props.userState.user.currentToken
                }
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Get filename from Content-Disposition header if present
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = docName;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            // Convert response to blob
            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setAlertMessage('Failed to download file');
            setIsSuccess(false);
            setShowAlert(true);
            console.error('Error downloading file:', error);
        }
    };

    const updateDocStatus = async (docLinkId: number, status: ClaimStatus) => {
        setIsUpdatingStatus(true);
        try {
            const response = await fetch(`${baseEndpoint}propertyownershipclaims/doclink/${docLinkId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${props.userState.user.currentToken}`
                },
                body: JSON.stringify(status)
            });

            if (!response.ok) {
                throw new Error('Failed to update document status');
            }

            if (claim) {
                setClaim({
                    ...claim,
                    docs: claim.docs?.map(doc =>
                        doc.internalId === docLinkId
                            ? { ...doc, status }
                            : doc
                    ) || []
                });
            }

            setAlertMessage(status === ClaimStatus.Accepted ?
                'Document accepted successfully!' :
                'Document rejected successfully!'
            );
            setIsSuccess(true);
            setShowAlert(true);

        } catch (error) {
            console.error('Error updating document status:', error);
            setAlertMessage('Failed to update document status');
            setIsSuccess(false);
            setShowAlert(true);
        } finally {
            setIsUpdatingStatus(false);
        }
    };


    const handleAccept = (docLinkId: number) => {
        updateDocStatus(docLinkId, ClaimStatus.Accepted);
    };

    const handleReject = (docLinkId: number) => {
        updateDocStatus(docLinkId, ClaimStatus.Rejected);
    };

    const getFileName = useCallback((path: string) => {
        return path.split('\\').pop()?.split('/').pop() || path;
    }, []);

    const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setClaimNotes(event.target.value);
    };

    const handleSaveNotes = async () => {
        if (!claim) return;

        setIsSaving(true);
        setSaveError(null);

        try {
            const response = await fetch(`${baseEndpoint}propertyownershipclaims/notes/${claim.internalId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${props.userState.user.currentToken}`
                },
                body: JSON.stringify(claimNotes)
            });

            if (!response.ok) {
                throw new Error('Failed to save notes');
            }
            setAlertMessage('Notes saved successfully!');
            setIsSuccess(true);
            setShowAlert(true);

        } catch (error) {
            console.error('Error saving notes:', error);
            setSaveError('Failed to save notes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAcceptClaim = () => {
        handleUpdateClaimStatus(ClaimStatus.Accepted);
    }

    const handleRejectClaim = () => {
        handleUpdateClaimStatus(ClaimStatus.Rejected);
    }

    const handleUpdateClaimStatus = async (status: ClaimStatus) => {
        if (!claim) return;
        setIsUpdatingClaim(true);  // Start loading
        try {
            const response = await fetch(`${baseEndpoint}propertyownershipclaims/${claim.internalId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${props.userState.user.currentToken}`
                },
                body: JSON.stringify(status)
            });

            if (!response.ok) {
                throw new Error('Failed to update claim status');
            }

            const updatedClaim = await response.json();

            setAlertMessage(status === ClaimStatus.Accepted ?
                'Document accepted successfully!' :
                'Document rejected successfully!'
            );
            setIsSuccess(true);
            setShowAlert(true);
            props.onClaimUpdated(updatedClaim);

        } catch (error) {
            console.error('Error updateing claim status:', error);
            setAlertMessage('Failed to update claim status');
            setIsSuccess(false);
            setShowAlert(true);
        } finally {
            setIsUpdatingClaim(false);  // End loading regardless of outcome
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFiles(event.target.files);
        }
    };

    const handleUpload = async () => {
        if (!selectedFiles || !claim) return;

        setIsUploading(true);
        const formData = new FormData();

        Array.from(selectedFiles).forEach(file => {
            formData.append('files', file);
        });

        try {
            const response = await fetch(`${baseEndpoint}properties/claim/upload/${claim.internalId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${props.userState.user.currentToken}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            setAlertMessage('Documents uploaded successfully!');
            setIsSuccess(true);
            setShowAlert(true);
            setSelectedFiles(null);

            // Refresh the claim data to show new documents
            // You might need to implement this refresh logic
            if (props.onClaimUpdated) {
                const updatedClaim = await response.json();
                props.onClaimUpdated(updatedClaim);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            setAlertMessage('Failed to upload documents');
            setIsSuccess(false);
            setShowAlert(true);
        } finally {
            setIsUploading(false);
        }
    };

    console.log(props.userState.user)
    const renderDialogTitle = () => {
        const address = claim?.address ?
            `${claim.address.address}` :
            'Property';

        if (props.userState.user.category === 1) {
            return `View Claim By: ${claim?.user?.firstName} ${claim?.user?.lastName}`;
        } else {
            return `View Claim for: ${address}`;
        }
    };

    const renderAnswerInput = (question: Question) => {
        const questionAnswers = props.answers.filter(a => a.questionId === question.internalId);
        const currentAnswer = verificationAnswers.find(a => a.questionId === question.internalId);

        switch (question.answerType) {
            case AnswerType.Boolean:
                return (
                    <FormControl component="fieldset" className={classes.answerInput}>
                        <FormLabel component="legend">Your Answer</FormLabel>
                        <RadioGroup
                            row
                            className={classes.radioGroup}
                            value={currentAnswer?.internalId || ''}
                            onChange={(e) => handleAnswerChange(question.internalId, Number(e.target.value))}
                        >
                            {questionAnswers.map(answer => (
                                <FormControlLabel 
                                    key={answer.internalId}
                                    value={answer.internalId} 
                                    control={<Radio />} 
                                    label={answer.description} 
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                );

            case AnswerType.String:
                return (
                    <TextField
                        className={classes.answerInput}
                        multiline
                        rows={3}
                        variant="outlined"
                        label="Your Answer"
                        value={currentAnswer?.description || ''}
                        onChange={(e) => handleAnswerChange(question.internalId, e.target.value)}
                    />
                );

            case AnswerType.Number:
                return (
                    <TextField
                        className={classes.answerInput}
                        type="number"
                        variant="outlined"
                        label="Your Answer"
                        value={currentAnswer?.description || ''}
                        onChange={(e) => handleAnswerChange(question.internalId, Number(e.target.value))}
                    />
                );

            default:
                return null;
        }
    };

    const handleAnswerChange = (questionId: number, value: any) => {
        const newAnswer: Answer = {
            internalId: Date.now(), // Temporary ID for new answers
            id: `ANS_${Date.now()}`, // Temporary ID string
            description: value.toString(),
            score: 0, // This will be calculated on the backend
            questionId: questionId,
            createdBy: props.userState.user.internalId,
            createdUtc: new Date()
        };

        setVerificationAnswers(prev => {
            const existingIndex = prev.findIndex(a => a.questionId === questionId);
            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = newAnswer;
                return updated;
            }
            return [...prev, newAnswer];
        });
    };

    const calculateTotalScore = (answers: Answer[]): number => {
        return answers.reduce((total, answer) => {
            // Only add the score if an answer was selected
            if (answer.internalId) {
                return total + (answer.score || 0);
            }
            return total;
        }, 0);
    };

    return (
        <Dialog
            TransitionComponent={Transition}
            fullScreen
            open={props.open}
            onClose={() => {
                setActiveTab(0);
                props.onClose();
            }}
        >
            <AppBar className={classes.appBar}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => {
                        setActiveTab(0);
                        props.onClose();
                    }} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        {renderDialogTitle()}
                    </Typography>
                </Toolbar>
            </AppBar>
            <DialogContent>
                {claim?.status === ClaimStatus.Accepted && <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className={classes.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    <Tab label="Documents" />
                    <Tab label="Verifications" />
                </Tabs>}
                {activeTab === 0 && <div style={{ flexGrow: 1 }}>
                    <Paper elevation={5} className={classes.root}>
                        <div className={classes.listItemContent}>
                            <div className={classes.addressContent}>
                                <img
                                    style={{ maxHeight: "100%" }}
                                    alt="Street View Not Found"
                                    src={streetViewUrl}
                                />
                                <div style={{ display: "block", paddingLeft: "15px" }}>
                                    <Typography variant='body1'>
                                        {claim?.address?.address}, {claim?.address?.city}, {claim?.address?.state} {claim?.address?.zip}
                                    </Typography>
                                    <div style={{ marginTop: 3 }}>
                                        <div className={classes.scoreDisplay}>
                                            <img src="../leaf-score.png" alt="leaf" />
                                            <Typography 
                                                variant="h2"
                                                className={classes.scoreText}
                                            >
                                                <strong>
                                                    {claim?.property ? 
                                                        Math.round(claim.property.calcScore + calculateTotalScore(verificationAnswers))
                                                        : 'N/A'
                                                    }
                                                </strong>
                                            </Typography>
                                        </div>
                                        <Typography className={classes.scoreDescription} variant="body1">
                                            Verification Bonus: +{calculateTotalScore(verificationAnswers)} points
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.docsContainer}>
                                <Typography variant="h6" style={{ textAlign: 'center' }}>
                                    Documents
                                </Typography>
                                {claim?.docs && claim.docs.length > 0 ? (
                                    <List className={classes.docsList}>
                                        {claim.docs.map((doc, index) => (
                                            <ListItem
                                                key={index}
                                                className={`${classes.docItem} ${doc.status === 1 ? 'accepted' :
                                                    doc.status === 2 ? 'rejected' : ''
                                                    }`}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <span
                                                            className={classes.docLink}
                                                            onClick={() => handleDownload(doc.internalId, getFileName(doc.documentPath))}
                                                            style={{
                                                                cursor: doc.status === 2 ? 'not-allowed' : 'pointer',
                                                                color: doc.status === 2 ? '#666' : undefined
                                                            }}
                                                        >
                                                            {getFileName(doc.documentPath)}
                                                        </span>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <RenderDocumentActions
                                                        doc={doc}
                                                        classes={classes}
                                                        handleAccept={handleAccept}
                                                        handleReject={handleReject}
                                                        isUpdatingStatus={isUpdatingStatus}
                                                        userState={props.userState}
                                                    />
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <div className={classes.emptyDocsMessage}>
                                        <Typography variant="body1" style={{ textAlign: 'center', padding: '20px' }}>
                                            No documents have been uploaded yet.
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={classes.notesContainer}>
                            <Typography variant="h6" style={{ textAlign: 'left', marginLeft: "5%" }}>
                                Notes
                            </Typography>
                            <textarea
                                className={classes.notesTextArea}
                                value={claimNotes}
                                onChange={handleNotesChange}
                                placeholder={isAdmin ? "Add notes here..." : "No notes available"}
                                readOnly={!isAdmin}
                                style={{
                                    cursor: isAdmin ? 'text' : 'default',
                                    backgroundColor: isAdmin ? '#f5f5f5' : '#f8f8f8'
                                }}
                            />
                            {isAdmin && (
                                <div className={classes.buttonContainer}>
                                    {saveError && (
                                        <Typography color="error" style={{ marginRight: '16px' }}>
                                            {saveError}
                                        </Typography>
                                    )}
                                    <Button
                                        variant="contained"
                                        className={classes.saveButton}
                                        onClick={handleSaveNotes}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving...' : 'Save Notes'}
                                    </Button>
                                </div>
                            )}
                        </div>
                        {!isAdmin && (
                            <div className={classes.uploadSection}>
                                <Typography variant="h6" style={{ textAlign: 'center' }}>
                                    {!claim?.docs || claim.docs.length === 0
                                        ? 'Upload Ownership Documents'
                                        : claim.status === ClaimStatus.Rejected
                                            ? 'Upload Updated Documents'
                                            : 'Upload Additional Documents'}
                                </Typography>
                                <div className={classes.uploadButtonContainer}>
                                    <input
                                        accept="image/*,.pdf,.doc,.docx"
                                        className={classes.fileInput}
                                        id="contained-button-file"
                                        multiple
                                        type="file"
                                        onChange={handleFileSelect}
                                    />
                                    <label htmlFor="contained-button-file">
                                        <Button
                                            variant="contained"
                                            component="span"
                                            className={classes.uploadButton}
                                            startIcon={<CloudUploadIcon />}
                                            disabled={isUploading}
                                        >
                                            Select Files
                                        </Button>
                                    </label>
                                    {selectedFiles && (
                                        <Button
                                            variant="contained"
                                            className={classes.uploadButton}
                                            onClick={handleUpload}
                                            disabled={isUploading}
                                        >
                                            {isUploading ? (
                                                <>
                                                    <CircularProgress size={24} color="inherit" />
                                                    &nbsp;Uploading...
                                                </>
                                            ) : (
                                                'Upload Documents'
                                            )}
                                        </Button>
                                    )}
                                </div>
                                {selectedFiles && (
                                    <div className={classes.selectedFiles}>
                                        <Typography variant="subtitle1">Selected Files:</Typography>
                                        {Array.from(selectedFiles).map((file, index) => (
                                            <Typography key={index} variant="body2">
                                                {file.name}
                                            </Typography>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {isAdmin && <div className={classes.claimActionButtons}>
                            {claim?.status === ClaimStatus.Pending && (
                                <>
                                    <Button
                                        variant="contained"
                                        className={classes.claimAcceptButton}
                                        onClick={handleAcceptClaim}
                                        disabled={isUpdatingClaim}
                                    >
                                        {isUpdatingClaim ? 'Processing...' : 'Accept Claim'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        className={classes.claimRejectButton}
                                        onClick={handleRejectClaim}
                                        disabled={isUpdatingClaim}
                                    >
                                        {isUpdatingClaim ? 'Processing...' : 'Updates Required'}
                                    </Button>
                                </>
                            )}
                            {claim?.status === ClaimStatus.Accepted && (
                                <>
                                    <Typography className={classes.statusAccepted}>
                                        <CheckCircleIcon /> Claim Accepted
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        className={classes.claimRejectButton}
                                        onClick={handleRejectClaim}
                                        disabled={isUpdatingClaim}
                                    >
                                        {isUpdatingClaim ? 'Processing...' : 'Updates Required'}
                                    </Button>
                                </>
                            )}
                            {claim?.status === ClaimStatus.Rejected && (
                                <>
                                    <Typography className={classes.statusRejected}>
                                        <ErrorIcon /> Updates Required
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        className={classes.claimAcceptButton}
                                        onClick={handleAcceptClaim}
                                        disabled={isUpdatingClaim}
                                    >
                                        {isUpdatingClaim ? 'Processing...' : 'Accept Claim'}
                                    </Button>
                                </>
                            )}
                        </div>}
                    </Paper>
                </div>}
                {activeTab === 1 && <div style={{ flexGrow: 1 }}>
                    <Paper elevation={5} className={classes.root}>
                        <div className={classes.verificationContainer}>
                            <div className={classes.verificationHeader}>
                                <div className={classes.scoreDisplay}>
                                    <img 
                                        src="../leaf-score.png" 
                                        alt="leaf" 
                                        style={{ width: "100%" }}
                                    />
                                    <Typography 
                                        variant="h2" 
                                        className={classes.scoreText}
                                    >
                                        <strong>
                                            {claim?.property ? 
                                                Math.round(claim.property.calcScore + calculateTotalScore(verificationAnswers))
                                                : 'N/A'
                                            }
                                        </strong>
                                    </Typography>
                                </div>
                                <div className={classes.scoreInfo}>
                                    <Typography variant="body1">
                                        Verification Bonus: +{calculateTotalScore(verificationAnswers)} points
                                    </Typography>
                                </div>
                            </div>

                            <Typography variant="h6" style={{ marginBottom: '20px', textAlign: 'center' }}>
                                Increase your building's Parslii Score!
                            </Typography>
                            
                            {verificationQuestions
                                .filter(q => q.parentQuestionId === 0) // Get parent categories
                                .map(category => (
                                    <Accordion key={category.internalId} className={classes.accordion}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            className={classes.accordionSummary}
                                        >
                                            <Typography variant="subtitle1">
                                                {category.description}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails style={{ display: 'block' }}>
                                            {verificationQuestions
                                                .filter(q => q.parentQuestionId === category.internalId)
                                                .map(question => (
                                                    <div key={question.internalId} className={classes.questionItem}>
                                                        <Typography variant="body1">
                                                            {question.description}
                                                            <Chip 
                                                                label={`${question.maxScore} points`}
                                                                size="small"
                                                                className={classes.scoreChip}
                                                            />
                                                        </Typography>
                                                        {renderAnswerInput(question)}
                                                    </div>
                                                ))
                                            }
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            }
                        </div>
                    </Paper>
                </div>}
            </DialogContent>
            <Snackbar
                open={showAlert}
                autoHideDuration={SNACKBAR_DURATION}
                onClose={() => setShowAlert(false)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}

            >
                <Alert
                    onClose={() => setShowAlert(false)}
                    variant="filled"
                    severity={isSuccess ? 'success' : 'error'}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}

const mapStateToProps = (state: any) => ({
    userState: state.userState
});


export default connect(mapStateToProps)(LandlordClaimsView);